import 'driver.js/dist/driver.css';
import './AiModeGuidedTour.css';
import { driver } from 'driver.js';

import { isValidURL } from '@/utils/validators';

const STEPS = {
  GOAL: 'goal',
  BRAND: 'brand',
  CONVERSION: 'conversion',
  SOURCE: 'source',
  LEADGEN: 'leadgen',
  CTA_URL: 'cta_url',
  BUDGET: 'budget',
  DSA: 'dsa',
  GEOTARGET: 'geotarget',
  SUMMARY: 'summary',
};

export default class AiModeGuidedTour {
  constructor(vm) {
    this.vm = vm;
    this.driver = null;
    this.unwatch = null;
    this.currentStep = null;
    this.scrollSyncRaf = null;
    this.scrollRefreshRaf = null;
    this.scrollListener = null;
    this.resizeObserver = null;
  }

  start() {
    this.driver = driver({
      stageRadius: 10,
      stagePadding: 20,
      allowClose: true,
      overlayClickBehavior: () => {
        // Keep accidental overlay clicks from closing the tour.
      },
      allowInteraction: true,
      disableActiveInteraction: false,
      smoothScroll: true,
      onCloseClick: () => this.end(),
    });
    this._attachScrollListener();
    this._showGoalStep(false);
  }

  end() {
    this._stopWatcher();
    this._stopScrollSync();
    this._detachScrollListener();
    this._stopResizeObserver();

    if (this.driver) {
      this.driver.destroy();
      this.driver = null;
    }

    const query = { ...this.vm.$route.query };
    if ('guided' in query) {
      delete query.guided;
      this.vm.$router.replace({ query });
    }
  }

  _tr(key, params) {
    return this.vm.tr(key, params);
  }

  _hasGoal() {
    return !!this.vm.goal;
  }

  _hasBrand() {
    return !!(this.vm.entity.brand && this.vm.entity.brand.id);
  }

  _hasConversion() {
    const s = this.vm.entity.settings;
    if (s.adset_conversion_location === 'app') {
      return !!s.adset_conversion_app_id && !!s.adset_conversion_app_store_url;
    }
    return !!s.adset_conversion_pixel_id && !!s.adset_conversion_event;
  }

  _hasSource() {
    const profiles = this.vm.entity.source_profiles || [];
    return profiles.length > 0 && profiles.every(p => p && p.id);
  }

  _hasLeadgenForm() {
    return !!this.vm.entity.settings.leadgen_form_id;
  }

  _hasBudget() {
    const c = this.vm.entity.output_campaign;
    return !!(c && c.budget && Number(c.budget) > 0);
  }

  _hasDsa() {
    const e = this.vm.entity;
    return !!(
      e.dsa_beneficiary && e.dsa_beneficiary.trim()
      && e.dsa_payer && e.dsa_payer.trim()
    );
  }

  _hasCtaUrl() {
    const url = this.vm.entity.settings.cta_url;
    return !!(url && url.trim());
  }

  _hasGeoTarget() {
    const t = this.vm.entity.settings.adset_targeting;
    return !!(
      t
      && t.geo_locations
      && t.geo_locations.all
      && t.geo_locations.all.length > 0
    );
  }

  _stopWatcher() {
    if (this.unwatch) {
      this.unwatch();
      this.unwatch = null;
    }
  }

  _stopScrollSync() {
    if (this.scrollSyncRaf !== null) {
      cancelAnimationFrame(this.scrollSyncRaf);
      this.scrollSyncRaf = null;
    }
  }

  _stopScrollRefresh() {
    if (this.scrollRefreshRaf !== null) {
      cancelAnimationFrame(this.scrollRefreshRaf);
      this.scrollRefreshRaf = null;
    }
  }

  _attachScrollListener() {
    if (this.scrollListener || typeof window === 'undefined') {
      return;
    }
    this.scrollListener = () => {
      if (!this.driver || this.scrollRefreshRaf !== null) {
        return;
      }
      this.scrollRefreshRaf = requestAnimationFrame(() => {
        this.scrollRefreshRaf = null;
        if (this.driver) {
          this.driver.refresh();
        }
      });
    };
    window.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  _detachScrollListener() {
    if (!this.scrollListener || typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('scroll', this.scrollListener);
    this.scrollListener = null;
    this._stopScrollRefresh();
  }

  _stopResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  _highlight(config) {
    this._stopResizeObserver();
    this.driver.highlight(config);
    this._syncPopoverWithScroll();
    this._observeHighlightedElement(config.element);
  }

  _observeHighlightedElement(element) {
    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => {
      if (this.driver) {
        this.driver.refresh();
      }
    });
    this.resizeObserver.observe(element);
  }

  _syncPopoverWithScroll() {
    this._stopScrollSync();

    let lastScrollY = window.scrollY;
    let stableFrames = 0;
    const startedAt = performance.now();

    const tick = () => {
      if (!this.driver) {
        this.scrollSyncRaf = null;
        return;
      }

      const currentY = window.scrollY;
      if (currentY === lastScrollY) {
        stableFrames++;
      }
      else {
        stableFrames = 0;
        lastScrollY = currentY;
        this.driver.refresh();
      }

      if (stableFrames >= 3 || performance.now() - startedAt > 2000) {
        this.driver.refresh();
        this.scrollSyncRaf = null;
        return;
      }

      this.scrollSyncRaf = requestAnimationFrame(tick);
    };

    this.scrollSyncRaf = requestAnimationFrame(tick);
  }

  _watchUntil(getter) {
    this._stopWatcher();
    this.unwatch = this.vm.$watch(
      getter,
      val => {
        if (val) {
          this._stopWatcher();
          this.vm.$nextTick(() => this._next());
        }
      },
    );
  }

  _next() {
    this.vm.$toast.clear();

    switch (this.currentStep) {
      case STEPS.GOAL:
        if (!this._hasGoal()) {
          this.vm.error(this._tr('Defining the goal is mandatory.'));
          return;
        }
        this._showBrandStep();
        break;

      case STEPS.BRAND:
        if (!this.vm.$refs.brandBlock.validate()) {
          return;
        }
        if (this.vm.conversionEventSettingsAvailable) {
          this._showConversionStep();
        }
        else {
          this._showSourceStep();
        }
        break;

      case STEPS.CONVERSION:
        if (!this.vm.$refs.conversionOdaxBlock.validate()) {
          return;
        }
        this._showSourceStep();
        break;

      case STEPS.SOURCE:
        if (!this.vm.$refs.sourceBlock.validate()) {
          return;
        }
        if (this.vm.goal === 'leads') {
          this._showLeadgenStep();
        }
        else if (this.vm.ctaUrlIsAvailable) {
          this._showCtaUrlStep();
        }
        else {
          this._showBudgetStep();
        }
        break;

      case STEPS.LEADGEN:
        if (!this.vm.$refs.leadgenFormBlock.validate()) {
          return;
        }
        this._showBudgetStep();
        break;

      case STEPS.CTA_URL: {
        const url = this.vm.entity.settings.cta_url;
        if (!url || !isValidURL(url)) {
          this.vm.error(this._tr('Invalid website URL.'));
          return;
        }
        this._showBudgetStep();
        break;
      }

      case STEPS.BUDGET:
        if (!this.vm.$refs.campaignBudgetBlock.validate()) {
          return;
        }
        this._showDsaStep();
        break;

      case STEPS.DSA:
        if (!this.vm.$refs.dsaBlock.validate()) {
          return;
        }
        if (this._hasGeoTarget()) {
          this._showSummaryStep();
        }
        else {
          this._showGeoTargetStep();
        }
        break;

      case STEPS.GEOTARGET:
        if (!this.vm.$refs.geotargetBlock.validate()) {
          return;
        }
        this._showSummaryStep();
        break;

      case STEPS.SUMMARY:
        this.end();
        break;
    }
  }

  _addChangeGoalButton(popover) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'driver-popover-footer-btn change-goal-btn';
    btn.innerText = this._tr('Change goal');
    btn.addEventListener('click', () => {
      this._showGoalStep(true);
    });
    popover.footerButtons.prepend(btn);
    if (popover.footer) {
      if (popover.footer.style.display === 'none') {
        if (popover.previousButton) {
          popover.previousButton.style.display = 'none';
        }
        if (popover.nextButton) {
          popover.nextButton.style.display = 'none';
        }
        if (popover.progress) {
          popover.progress.style.display = 'none';
        }
      }
      popover.footer.style.display = 'flex';
    }
  }

  _showGoalStep(isChangeMode) {
    this.currentStep = STEPS.GOAL;
    const el = this.vm.$el.querySelector('.goal-block');
    const goalSelected = this._hasGoal();

    let title;
    let description;
    let showButtons;

    if (isChangeMode) {
      title = this._tr('Select a new advertising goal');
      description = this._tr('Please select a new advertising goal from the options below.');
      showButtons = [ 'next', 'close' ];
    }
    else {
      title = this._tr('Advertising goal');
      if (goalSelected) {
        description = this._tr('When creating an automation, the first step is to define the advertising goal. We have automatically applied the goal you selected in the previous step.');
        showButtons = [ 'next', 'close' ];
      }
      else {
        description = this._tr('When creating an automation, the first step is to define the advertising goal.');
        showButtons = [ 'close' ];
      }
    }

    this._highlight({
      element: el,
      popover: {
        title,
        description,
        nextBtnText: this._tr('Next'),
        showButtons,
        onNextClick: () => this._next(),
      },
    });

    if (!goalSelected && !isChangeMode) {
      this._watchUntil(() => this._hasGoal());
    }
    else {
      this._stopWatcher();
    }
  }

  _showBrandStep() {
    this.currentStep = STEPS.BRAND;
    const hasValue = this._hasBrand();
    const description = hasValue
      ? this._tr('The brand determines the ad account, and we use it to pre-fill the audience and website settings as well. We have already selected one for you - change it here if you would prefer a different brand.')
      : this._tr('Pick which brand this automation belongs to. The brand determines the ad account, and we use it to pre-fill the audience and website settings as well.');

    this._highlight({
      element: this.vm.$refs.brandBlock.$el,
      popover: {
        title: this._tr('Brand selection'),
        description,
        nextBtnText: this._tr('Next'),
        showButtons: hasValue ? [ 'next', 'close' ] : [ 'close' ],
        onNextClick: () => this._next(),
      },
    });

    if (!hasValue) {
      this._watchUntil(() => this._hasBrand());
    }
    else {
      this._stopWatcher();
    }
  }

  _showConversionStep() {
    this.currentStep = STEPS.CONVERSION;
    this.vm.$nextTick(() => {
      const hasValue = this._hasConversion();
      const description = hasValue
        ? this._tr('For this goal a conversion event is required so Meta can measure the campaign\'s performance. We have already filled in the appropriate values - change them here if you need something different.')
        : this._tr('For this goal a conversion event is required so Meta can measure the campaign\'s performance. If you do not see the right pixel or app in the list, you may want to change the advertising goal.');

      this._highlight({
        element: this.vm.$refs.conversionOdaxBlock.$el,
        popover: {
          title: this._tr('Conversion settings'),
          description,
          nextBtnText: this._tr('Next'),
          showButtons: hasValue ? [ 'next', 'close' ] : [ 'close' ],
          onPopoverRender: popover => {
            this._addChangeGoalButton(popover);
          },
          onNextClick: () => this._next(),
        },
      });

      if (!hasValue) {
        this._watchUntil(() => this._hasConversion());
      }
      else {
        this._stopWatcher();
      }
    });
  }

  _showSourceStep() {
    this.currentStep = STEPS.SOURCE;
    const hasValue = this._hasSource();
    const description = hasValue
      ? this._tr('Choose the Facebook page or Instagram account from which the ads should run. We have set the source based on the brand - change it here if you would prefer a different one.')
      : this._tr('Choose the Facebook page or Instagram account from which the ads should run.');

    this._highlight({
      element: this.vm.$refs.sourceBlock.$el,
      popover: {
        title: this._tr('Source'),
        description,
        nextBtnText: this._tr('Next'),
        showButtons: hasValue ? [ 'next', 'close' ] : [ 'close' ],
        onNextClick: () => this._next(),
      },
    });

    if (!hasValue) {
      this._watchUntil(() => this._hasSource());
    }
    else {
      this._stopWatcher();
    }
  }

  _showLeadgenStep() {
    this.currentStep = STEPS.LEADGEN;
    this.vm.$nextTick(() => {
      const hasValue = this._hasLeadgenForm();
      const description = hasValue
        ? this._tr('Meta collects the contact details directly through the form, so the user does not have to leave the platform. We have already selected a form for you - change it here if you would prefer a different one.')
        : this._tr('Pick which previously created form should appear on the ad. Meta collects the contact details directly through the form, so the user does not have to leave the platform. If you set the wrong goal, you can change it here.');

      this._highlight({
        element: this.vm.$refs.leadgenFormBlock.$el,
        popover: {
          title: this._tr('Lead generation form'),
          description,
          nextBtnText: this._tr('Next'),
          showButtons: hasValue ? [ 'next', 'close' ] : [ 'close' ],
          onPopoverRender: popover => {
            this._addChangeGoalButton(popover);
          },
          onNextClick: () => this._next(),
        },
      });

      if (!hasValue) {
        this._watchUntil(() => this._hasLeadgenForm());
      }
      else {
        this._stopWatcher();
      }
    });
  }

  _showCtaUrlStep() {
    this.currentStep = STEPS.CTA_URL;
    this._stopWatcher();

    const hasValue = this._hasCtaUrl();
    const description = hasValue
      ? this._tr('We have already filled in this URL based on the website set in the brand settings - change it here if you want to promote a different page.')
      : this._tr('Provide the URL you want to promote with this ad. If you set a website at the brand level, we apply it automatically when the brand is selected. Since this brand has no website set, you need to enter the URL here.');

    this._highlight({
      element: this.vm.$refs.ctaUrlBlock.$el,
      popover: {
        title: this.vm.ctaUrlLabel,
        description,
        nextBtnText: this._tr('Next'),
        showButtons: [ 'next', 'close' ],
        onNextClick: () => this._next(),
      },
    });
  }

  _showBudgetStep() {
    this.currentStep = STEPS.BUDGET;
    this._stopWatcher();

    const hasValue = this._hasBudget();
    const description = hasValue
      ? this._tr('Set the maximum amount you want to spend on ads per day. This cap applies to all ads launched by this automation together. We have already filled in a daily budget - change it here if you would like a different value.')
      : this._tr('Set the maximum amount you want to spend on ads per day. This cap applies to all ads launched by this automation together.');

    this._highlight({
      element: this.vm.$refs.campaignBudgetBlock.$el,
      popover: {
        title: this._tr('Daily budget'),
        description,
        nextBtnText: this._tr('Next'),
        showButtons: [ 'next', 'close' ],
        onNextClick: () => this._next(),
      },
    });
  }

  _showDsaStep() {
    this.currentStep = STEPS.DSA;
    this._stopWatcher();

    const hasValue = this._hasDsa();
    const description = hasValue
      ? this._tr('For ads targeted at the EU, Meta requires you to declare the beneficiary and the payer of the ad. We have already filled in the defaults from your company data - change them here if Meta expects different names.')
      : this._tr('For ads targeted at the EU, Meta requires you to declare the beneficiary and the payer of the ad.');

    this._highlight({
      element: this.vm.$refs.dsaBlock.$el,
      popover: {
        title: this._tr('Beneficiary and payer'),
        description,
        nextBtnText: this._tr('Next'),
        showButtons: [ 'next', 'close' ],
        onNextClick: () => this._next(),
      },
    });
  }

  _showGeoTargetStep() {
    this.currentStep = STEPS.GEOTARGET;
    this._stopWatcher();

    this._highlight({
      element: this.vm.$refs.geotargetWrapper,
      popover: {
        title: this._tr('Where to display the ad'),
        description: this._tr('If you set a default targeting at the brand level, we apply it automatically when the brand is selected. Since this brand has no default targeting set, you need to specify here where your ad should be shown.'),
        nextBtnText: this._tr('Next'),
        showButtons: [ 'next', 'close' ],
        onNextClick: () => this._next(),
      },
    });
  }

  _showSummaryStep() {
    this.currentStep = STEPS.SUMMARY;
    this._stopWatcher();

    this._highlight({
      popover: {
        title: this._tr('You are all set!'),
        description: this._tr('We have configured the most important settings of your automation. Feel free to fine-tune additional details on this page, or move straight on to the next step.'),
        nextBtnText: this._tr('Continue'),
        showButtons: [ 'next' ],
        popoverClass: 'driver-popover--summary',
        onNextClick: () => this._next(),
      },
    });
  }
}
